/**
 * RiderDeductionFilterSection Component
 * 라이더 선정산내역 검색 필터 영역
 */
import React, { useCallback, useMemo } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { FilterState } from '../types/riderPreSettlement.types';
import '@/shared/styles/search-form.css';
import '../styles/RiderPreSettlementHistory.css';

interface Supplier {
    id: string;
    name: string;
}

interface FilterSectionProps {
    filters: FilterState;
    suppliers: Supplier[];
    isSupplierUser: boolean;
    onFilterChange: (key: string, value: string) => void;
    onSearch: () => void;
    onReset: () => void;
    onExcelDownload: () => void;
}

/** 날짜 포맷팅 (yyyy-MM-dd) */
const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** 기간 프리셋 타입 */
type DatePreset = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

/** 프리셋별 날짜 범위 계산 (단일 진실 공급원) */
const getDateRangeForPreset = (preset: DatePreset): { startDate: Date; endDate: Date } => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

    switch (preset) {
        case 'thisWeek': {
            const start = new Date(today);
            start.setDate(today.getDate() + diffToMonday);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            return { startDate: start, endDate: end };
        }
        case 'lastWeek': {
            const start = new Date(today);
            start.setDate(today.getDate() + diffToMonday - 7);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            return { startDate: start, endDate: end };
        }
        case 'thisMonth':
            return {
                startDate: new Date(today.getFullYear(), today.getMonth(), 1),
                endDate: new Date(today.getFullYear(), today.getMonth() + 1, 0)
            };
        case 'lastMonth':
            return {
                startDate: new Date(today.getFullYear(), today.getMonth() - 1, 1),
                endDate: new Date(today.getFullYear(), today.getMonth(), 0)
            };
    }
};

const PRESETS: DatePreset[] = ['thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'];
const PRESET_LABELS: Record<DatePreset, string> = {
    thisWeek: '이번 주',
    lastWeek: '지난 주',
    thisMonth: '이번 달',
    lastMonth: '지난 달'
};

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         filters,
                                                         suppliers,
                                                         isSupplierUser,
                                                         onFilterChange,
                                                         onSearch,
                                                         onReset,
                                                         onExcelDownload
                                                     }) => {
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    const handlePresetClick = useCallback((preset: DatePreset) => {
        const { startDate, endDate } = getDateRangeForPreset(preset);
        onFilterChange('acctStDate', formatDate(startDate));
        onFilterChange('acctEdDate', formatDate(endDate));
    }, [onFilterChange]);

    const activePreset = useMemo((): DatePreset | null => {
        if (!filters.acctStDate || !filters.acctEdDate) return null;

        for (const preset of PRESETS) {
            const { startDate, endDate } = getDateRangeForPreset(preset);
            if (filters.acctStDate === formatDate(startDate) && filters.acctEdDate === formatDate(endDate)) {
                return preset;
            }
        }
        return null;
    }, [filters.acctStDate, filters.acctEdDate]);

    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier) => ({
            value: supplier.id,
            label: supplier.name
        }));
    }, [suppliers]);

    const getFormClassName = () => {
        if (isSupplierUser) {
            return 'search-form rider-pre-settlement-search-form-supplier';
        }
        return 'search-form rider-pre-settlement-search-form-hq';
    };

    return (
        <div className="card search-card rider-pre-settlement-filter">
            <div className="card-body">
                <div className={getFormClassName()}>
                    {/* 조회일자 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <div className="form-label-row">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    조회일자
                                </label>
                                <div className="date-preset-buttons">
                                    {PRESETS.map(preset => (
                                        <button
                                            key={preset}
                                            type="button"
                                            className={`date-preset-btn ${activePreset === preset ? 'active' : ''}`}
                                            onClick={() => handlePresetClick(preset)}
                                        >
                                            {PRESET_LABELS[preset]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="date-range-inputs">
                                <DateInput
                                    id="filterAcctStDate"
                                    value={filters.acctStDate}
                                    onChange={(value) => onFilterChange('acctStDate', value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <span className="date-separator">~</span>
                                <DateInput
                                    id="filterAcctEdDate"
                                    value={filters.acctEdDate}
                                    onChange={(value) => onFilterChange('acctEdDate', value)}
                                    onKeyPress={handleKeyPress}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 라이더명 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-user" />
                                라이더명
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={filters.riderName}
                                onChange={(e) => onFilterChange('riderName', e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="라이더명 입력..."
                            />
                        </div>
                    </div>

                    {/* 협력사 (본사 사용자만) */}
                    {!isSupplierUser && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filters.supplierId}
                                    onChange={(value) => onFilterChange('supplierId', value)}
                                    placeholder="협력사 검색..."
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        {!isSupplierUser && (
                            <button className="btn btn-success" onClick={onExcelDownload}>
                                <i className="fas fa-file-excel" />
                                엑셀
                            </button>
                        )}
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            조회
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
